import { useState, useEffect, useCallback } from 'react';
import { Sparkles, RefreshCw, Check, X } from 'lucide-react';
import {
  runSkillMining,
  listSkillMiningProposals,
  adoptSkillMiningProposal,
  rejectSkillMiningProposal,
  type PrecipitationProposal,
} from '../api/skillMining';
import ConfigDiffViewer from './ConfigDiffViewer';
import { useToast } from './ui/toastContext';

const getApiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

const STATUS_LABELS: Record<string, { text: string; color: string }> = {
  pending: { text: '待确认', color: 'var(--color-warning, #d29922)' },
  verified: { text: '已验证', color: 'var(--color-success)' },
  adopted: { text: '已采纳', color: 'var(--text-secondary, #8b949e)' },
  rejected: { text: '已拒绝', color: 'var(--text-secondary, #8b949e)' },
};

const KIND_LABELS: Record<string, string> = {
  new_skill: '建议新技能',
  skill_improvement: '技能改进建议',
};

/** B17 信任档位徽标：沉淀采纳的新技能默认进入隔离观察期（quarantined） */
const TRUST_BADGE_STYLES: Record<string, { label: string; color: string }> = {
  quarantined: { label: '隔离中', color: 'var(--color-warning, #d29922)' },
  active: { label: '已激活', color: 'var(--text-secondary, #8b949e)' },
  global: { label: '全局', color: 'var(--color-success)' },
};

/** 提案的 diff 预览：技能改进用 before/after 载荷，新技能展示建议载荷 JSON。 */
function ProposalDiff({ proposal }: { proposal: PrecipitationProposal }) {
  const payload = (proposal.payload || {}) as {
    skill_name?: string;
    field?: string;
    before?: string;
    after?: string;
    name?: string;
    display_name?: string;
    description?: string;
  };
  if (proposal.kind === 'skill_improvement' && typeof payload.before === 'string') {
    return (
      <ConfigDiffViewer
        left={payload.before ?? ''}
        right={payload.after ?? ''}
      />
    );
  }
  return (
    <pre
      style={{
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        margin: 0,
        padding: 8,
        background: 'var(--bg-secondary, #161b22)',
        borderRadius: 6,
        maxHeight: 200,
        overflow: 'auto',
      }}
    >
      {JSON.stringify(proposal.payload, null, 2)}
    </pre>
  );
}

/** 证据摘要：来源 + 会话数 + 失败样本（诚实展示挖掘依据）。 */
function EvidenceSummary({ proposal }: { proposal: PrecipitationProposal }) {
  const evidence = (proposal.evidence || {}) as {
    source_hint?: string;
    sessions?: unknown[];
    samples?: Array<{ session_id?: string; user_prompt?: string; rating?: string }>;
    usage_stats?: { skill_name?: string; uses?: number; negative_sessions?: number; failure_ratio?: number };
    reject_reason?: string;
  };
  const sourceLabel =
    evidence.source_hint === 'retry_chain'
      ? '负反馈后重问（retry 链）'
      : evidence.source_hint === 'repeated_intent'
        ? '跨会话重复意图'
        : evidence.source_hint === 'weak_skill'
          ? '高频引用但表现差'
          : (evidence.source_hint ?? '—');
  const sessions = evidence.sessions?.length ?? 0;
  const firstSample = evidence.samples?.[0];
  const stats = evidence.usage_stats;

  return (
    <div style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
      <div>
        来源：<strong>{sourceLabel}</strong> · 涉及会话 {sessions} 个
        {stats ? ` · 被引用 ${stats.uses} 次 / 负反馈 ${stats.negative_sessions} 个会话` : ''}
      </div>
      {firstSample?.user_prompt && (
        <div style={{ marginTop: 4 }}>
          证据样本：{String(firstSample.user_prompt).slice(0, 120)}
        </div>
      )}
      {evidence.reject_reason ? <div style={{ marginTop: 4 }}>拒绝原因：{evidence.reject_reason}</div> : null}
    </div>
  );
}

function ProposalCard({
  proposal,
  onDecision,
  busy,
}: {
  proposal: PrecipitationProposal;
  onDecision: (proposal: PrecipitationProposal, action: 'adopt' | 'reject') => Promise<void>;
  busy: boolean;
}) {
  const status = STATUS_LABELS[proposal.status] || { text: proposal.status, color: 'var(--text-secondary)' };
  const decidabled = proposal.status === 'pending' || proposal.status === 'verified';
  const payload = proposal.payload as { display_name?: string; name?: string; skill_name?: string };
  const title =
    proposal.kind === 'new_skill'
      ? payload.display_name || payload.name
      : `改进技能「${payload.skill_name}」`;

  return (
    <div className="card" style={{ background: 'var(--bg-primary)', marginBottom: 10 }}>
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <strong>{title}</strong>
          <span
            style={{
              fontSize: 12,
              padding: '2px 8px',
              borderRadius: 10,
              border: `1px solid ${status.color}`,
              color: status.color,
            }}
          >
            {status.text}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
            {KIND_LABELS[proposal.kind] || proposal.kind}
          </span>
          {proposal.adopted_skill_trust_state && (
            <span
              style={{
                fontSize: 12,
                padding: '2px 8px',
                borderRadius: 10,
                border: `1px solid ${TRUST_BADGE_STYLES[proposal.adopted_skill_trust_state]?.color || 'var(--text-secondary, #8b949e)'}`,
                color: TRUST_BADGE_STYLES[proposal.adopted_skill_trust_state]?.color || 'var(--text-secondary, #8b949e)',
              }}
              title="B17 信任档位：沉淀采纳的新技能进入隔离观察期，3 次成功使用后晋升"
            >
              {TRUST_BADGE_STYLES[proposal.adopted_skill_trust_state]?.label || proposal.adopted_skill_trust_state}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {proposal.score_before !== null && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
              {proposal.score_before.toFixed(2)} → {proposal.score_after?.toFixed(2) ?? '—'}
            </span>
          )}
          <button
            className="btn btn-sm"
            disabled={!decidabled || busy}
            onClick={() => onDecision(proposal, 'reject')}
            style={{ color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
          >
            <X size={14} /> 拒绝
          </button>
          <button
            className="btn btn-sm btn-primary"
            disabled={!decidabled || busy}
            onClick={() => onDecision(proposal, 'adopt')}
          >
            <Check size={14} /> 采纳
          </button>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <EvidenceSummary proposal={proposal} />
      </div>
      {proposal.model_drift?.drifted && proposal.model_drift.message && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            padding: '8px 10px',
            borderRadius: 6,
            border: '1px solid var(--color-warning)',
            background: 'rgba(240, 136, 62, 0.1)',
            color: 'var(--color-warning)',
          }}
        >
          ⚠ {proposal.model_drift.message}
        </div>
      )}
      {proposal.verification_note && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            padding: '6px 10px',
            borderRadius: 6,
            background: 'var(--bg-secondary, #161b22)',
            color: 'var(--text-secondary, #8b949e)',
          }}
        >
          {proposal.verification_note}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <ProposalDiff proposal={proposal} />
      </div>
    </div>
  );
}

export default function SkillMiningPanel({ agentId }: { agentId: string }) {
  const [proposals, setProposals] = useState<PrecipitationProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSkillMiningProposals(agentId);
      setProposals(data);
    } catch (e) {
      toast.error(`加载沉淀提案失败：${getApiErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState（react-hooks/set-state-in-effect）
    const tick = () => {
      void load();
    };
    tick();
  }, [load]);

  const handleRun = async () => {
    setRunning(true);
    try {
      const result = await runSkillMining(agentId, { window_days: 30 });
      toast.success(`离线沉淀挖掘已启动（任务 ${result.job_id}，纯启发式，零 LLM），正在后台执行…`);
      // 挖掘是后台任务：延迟拉取让提案落库可见（无 job SSE 依赖，失败诚实降级）
      setTimeout(() => void load(), 1500);
    } catch (e) {
      toast.error(`挖掘触发失败：${getApiErrorMessage(e)}`);
    } finally {
      setRunning(false);
    }
  };

  const handleDecision = async (proposal: PrecipitationProposal, action: 'adopt' | 'reject') => {
    setBusyId(proposal.id);
    try {
      if (action === 'adopt') {
        await adoptSkillMiningProposal(proposal.id);
        toast.success(proposal.kind === 'new_skill' ? '已采纳：生成禁用态技能草稿（可在技能维度启用）' : '已采纳：技能改进已应用');
      } else {
        await rejectSkillMiningProposal(proposal.id, '人工确认不需要');
        toast.success('已拒绝该提案');
      }
      await load();
    } catch (e) {
      toast.error(`操作失败：${getApiErrorMessage(e)}`);
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = proposals.filter((p) => p.status === 'pending' || p.status === 'verified').length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={16} style={{ color: 'var(--color-primary, #58a6ff)' }} />
          <h3 style={{ margin: 0 }}>离线沉淀（技能挖掘）</h3>
          {pendingCount > 0 && (
            <span style={{ fontSize: 12, color: 'var(--color-warning, #d29922)' }}>{pendingCount} 条待确认</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-sm" disabled={loading} onClick={() => void load()} title="刷新">
            <RefreshCw size={14} /> 刷新
          </button>
          <button className="btn btn-sm btn-primary" disabled={running} onClick={() => void handleRun()}>
            <Sparkles size={14} /> {running ? '挖掘中…' : '触发挖掘'}
          </button>
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)', margin: 0 }}>
        纯启发式（零 LLM）扫描历史会话：负反馈后重问（retry 链）、跨会话重复意图、以及被高频引用但表现差的技能。
        采纳新技能默认生成禁用态草稿；技能改进以 diff 形式供你确认。可在「定时任务」中添加 task_type=skill_mining 的夜间自动挖掘。
      </p>
      {loading && proposals.length === 0 ? (
        <div style={{ color: 'var(--text-secondary, #8b949e)' }}>加载中…</div>
      ) : proposals.length === 0 ? (
        <div className="card" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary, #8b949e)' }}>
          暂无沉淀提案。点击「触发挖掘」扫描近 30 天的教学会话与技能引用记录。
        </div>
      ) : (
        proposals.map((proposal) => (
          <ProposalCard
            key={proposal.id}
            proposal={proposal}
            busy={busyId === proposal.id}
            onDecision={handleDecision}
          />
        ))
      )}
    </div>
  );
}
